import { Zap, Brain, Network, BarChart3, Cog, Globe, Users, Clock, Shield } from 'lucide-react';

export const mockData = {
  hero: {
    title: "SAP + AI → Smarter Business",
    description: "Mahoga.ai is a consultancy focused on integrating advanced AI capabilities into SAP environments to help organizations operate with greater efficiency, agility, and insight."
  },
  
  why: {
    title: "More than a consultancy — a partner in intelligent transformation",
    description: "Mahoga.ai was founded on the belief that transformation should be agile, targeted, and results-driven. As a boutique firm with enterprise-grade expertise, we embed AI directly into SAP environments to help organizations operate with greater scalability, automation, and data intelligence.",
    differentiators: [
      {
        title: "AI-Native from the Start",
        description: "Purpose-built to integrate intelligence and automation into SAP systems."
      },
      {
        title: "Global Reach, Agile Delivery",
        description: "Teams in EMEA, NA, and APAC provide localized expertise with rapid execution."
      },
      {
        title: "Results-Oriented",
        description: "Focused on measurable business outcomes, not just deliverables."
      },
      {
        title: "Collaborative Partnership",
        description: "Engaged from strategy through ongoing support."
      },
      {
        title: "Engineering-Led Execution",
        description: "SAP specialists and AI engineers working in sync."
      }
    ]
  },

  solutions: {
    title: "Integrated. Intelligent. Enterprise-Grade.",
    description: "Mahoga.ai designs and delivers tailored SAP and AI solutions that enable enterprises to operate more efficiently, make informed decisions, and scale effectively. We combine technical depth with business insight, ensuring solutions are practical, sustainable, and aligned with strategic objectives.",
    services: [
      {
        icon: Zap,
        title: "AI-Enhanced SAP Application Development",
        description: "We develop intelligent applications on SAP BTP that streamline processes, improve usability, and extend system capabilities. Our approach incorporates SAP best practices with AI-driven enhancements, low-code/no-code tools, and modular design for scalability.",
        features: ["Fiori Apps", "Custom BTP Modules", "Intelligent Workflows", "UX Optimization"]
      },
      {
        icon: Brain,
        title: "AI Integration & Machine Learning Enablement",
        description: "We implement pre-trained and custom machine learning models directly within SAP environments to support predictive decision-making and real-time insight generation. Applications range from finance automation to supply chain optimization.",
        features: ["Predictive Forecasting", "Smart Chatbots", "Document Intelligence", "AI in Finance, Sales, Supply Chain, Operations & HR"]
      },
      {
        icon: Network,
        title: "End-to-End SAP Integration (CPI, APIs, Cloud)",
        description: "We connect SAP and third-party systems through API-first architecture, high-performance data flows, and cloud-native integration patterns—removing silos and enabling consistent data across the enterprise.",
        features: ["SAP Integration Suite", "Event-Driven Design", "API Lifecycle Management", "ETL Automation"]
      },
      {
        icon: BarChart3,
        title: "Data & Analytics Transformation",
        description: "We transform SAP data into actionable intelligence by creating unified data models, real-time dashboards, and embedded analytics. These solutions support decision-making from the executive level to daily operations.",
        features: ["Cloud Implementation", "KPI-Driven Dashboards", "Predictive/Prescriptive Analytics", "Finance & Operations Reporting"]
      },
      {
        icon: Cog,
        title: "RPA & Intelligent Automation",
        description: "We combine Robotic Process Automation with AI to eliminate repetitive tasks and improve process efficiency. Our intelligent automation solutions address scenarios from invoice processing to customer service.",
        features: ["SAP Integrated RPA", "Workflow Automation", "Self-Service", "AI-Driven Reasoning & Logic Handling"]
      }
    ]
  },

  industries: {
    title: "Built for Your World. Engineered for Your Needs.",
    description: "Each industry has its own priorities, processes, and challenges. Mahoga.ai delivers SAP and AI solutions designed to address the unique operational realities of each sector—aligning technology with business goals and regulatory requirements.",
    sectors: [
      {
        icon: Zap,
        name: "Energy & Utilities",
        value: "Smarter grid operations, predictive asset maintenance"
      },
      {
        icon: Cog,
        name: "Manufacturing",
        value: "Smart factories, AI-powered production planning, predictive maintenance"
      },
      {
        icon: Brain,
        name: "Pharma & Life Sciences",
        value: "Compliance automation, accelerated R&D, patient insights"
      },
      {
        icon: Network,
        name: "Transportation & Logistics",
        value: "Route optimization, real-time visibility, last-mile orchestration, warehouse automation"
      },
      {
        icon: BarChart3,
        name: "Healthcare",
        value: "Predictive care, clinical intelligence, cost optimization"
      },
      {
        icon: Shield,
        name: "Public Sector",
        value: "Citizen experience, secure data management, transparent service delivery"
      }
    ]
  },

  approach: {
    title: "Agile by Design. Outcome by Default.",
    description: "Digital transformation can be effective without being disruptive. Our approach builds on proven enterprise practices, refined for agility and modern delivery speed. We work within your existing architecture, strengthen what already works, and apply AI where it delivers the most impact.",
    phases: [
      {
        phase: "Phase 1: Discover & Diagnose",
        description: "Analyze systems, processes, and pain points to identify high-impact automation and AI opportunities."
      },
      {
        phase: "Phase 2: Design & Prototype",
        description: "Create solution blueprints and MVPs aligned with strategic priorities and operational realities."
      },
      {
        phase: "Phase 3: Develop & Integrate",
        description: "Build scalable solutions using SAP BTP, AI APIs, and modern data architecture, minimizing operational disruption."
      },
      {
        phase: "Phase 4: Measure & Optimize",
        description: "Define KPIs, track adoption and performance, and refine based on measurable results."
      },
      {
        phase: "Phase 5: Scale & Evolve",
        description: "Extend successful use cases across functions, geographies, and product lines to institutionalize innovation."
      }
    ]
  },

  impact: {
    title: "Measurable Results, Not Just Technology.",
    description: "Our work is defined by tangible improvements to operations, efficiency, and decision-making—measured against clear performance benchmarks.",
    metrics: [
      {
        value: "70%",
        description: "reduction in customer service response times"
      },
      {
        value: "40%",
        description: "decrease in manual processing"
      },
      {
        value: "60%",
        description: "increase in workflow automation"
      },
      {
        value: "2×",
        description: "improvement in analytics adoption"
      }
    ]
  },

  global: {
    title: "Think Big. Execute Anywhere.",
    description: "We deliver global expertise with local execution, combining international standards with regional knowledge. Our teams operate across time zones and markets, ensuring alignment with business objectives, compliance requirements, and cultural contexts.",
    capabilities: [
      {
        icon: Globe,
        title: "Global Presence",
        description: "Hubs in North America, EMEA, and APAC"
      },
      {
        icon: Clock,
        title: "24/7 Delivery",
        description: "Round-the-clock development and support capabilities"
      },
      {
        icon: Users,
        title: "Multicultural Teams",
        description: "Multilingual, diverse teams with regional expertise"
      },
      {
        icon: Shield,
        title: "Regulatory Alignment",
        description: "Region-aware compliance and regulatory requirements"
      }
    ]
  },

  contact: {
    title: "Let's Start the Conversation",
    description: "Every successful transformation begins with a clear discussion of goals and opportunities. Whether you are exploring AI-SAP integration or scaling for global growth, Mahoga.ai provides the expertise, innovation, and execution needed to deliver measurable results.",
    email: "info@mahoga.ai",
    website: "www.mahoga.ai",
    cta: "Book a Discovery Call"
  }
};